// TypeScript interfaces for AI service responses
export interface AIProductData {
  value: string;
  confidence: number;
  reasoning: string;
  sources?: string[];
}

export interface AIProductRequest {
  name: string;
  description?: string;
  category?: string;
  brand?: string;
}

export interface AIBatchResponse {
  manufacturer: AIProductData;
  originCountry: AIProductData;
  hsCode: AIProductData;
}

export interface AIServiceError {
  message: string;
  field?: 'manufacturer' | 'originCountry' | 'hsCode';
  type: 'network' | 'ai' | 'validation';
}

// Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001/api';

/**
 * AI Product Service for auto-populating International Shipping & Customs fields
 */
export class AIProductService {
  
  /**
   * Get manufacturer information for a product using AI
   */
  static async getManufacturer(productData: AIProductRequest): Promise<AIProductData> {
    try {
      const response = await fetch(`${API_BASE_URL}/ai/product/manufacturer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(productData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      
      // Debug logging
      console.log('AI Manufacturer Response:', responseData);
      
      // Extract data from wrapped response
      const result: AIProductData = responseData.data || responseData;
      
      // Validate response structure
      if (typeof result.value !== 'string' || typeof result.confidence !== 'number') {
        console.error('Invalid manufacturer response structure:', {
          value: result.value,
          valueType: typeof result.value,
          confidence: result.confidence,
          confidenceType: typeof result.confidence,
          fullResponse: responseData
        });
        throw new Error('Invalid AI response format');
      }

      return result;
    } catch (error) {
      console.error('Error getting manufacturer from AI:', error);
      throw {
        message: error instanceof Error ? error.message : 'Failed to get manufacturer information',
        field: 'manufacturer',
        type: error instanceof Error && error.message.includes('fetch') ? 'network' : 'ai'
      } as AIServiceError;
    }
  }

  /**
   * Get origin country for a product using AI
   */
  static async getOriginCountry(productData: AIProductRequest, manufacturer?: string): Promise<AIProductData> {
    try {
      const requestData = {
        ...productData,
        manufacturer: manufacturer
      };

      const response = await fetch(`${API_BASE_URL}/ai/product/origin-country`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      
      // Debug logging
      console.log('AI Origin Country Response:', responseData);
      
      // Extract data from wrapped response
      const result: AIProductData = responseData.data || responseData;
      
      // Validate response structure
      if (typeof result.value !== 'string' || typeof result.confidence !== 'number') {
        console.error('Invalid origin country response structure:', {
          value: result.value,
          valueType: typeof result.value,
          confidence: result.confidence,
          confidenceType: typeof result.confidence,
          fullResponse: responseData
        });
        throw new Error('Invalid AI response format');
      }

      return result;
    } catch (error) {
      console.error('Error getting origin country from AI:', error);
      throw {
        message: error instanceof Error ? error.message : 'Failed to get origin country information',
        field: 'originCountry',
        type: error instanceof Error && error.message.includes('fetch') ? 'network' : 'ai'
      } as AIServiceError;
    }
  }

  /**
   * Get HS Code for a product using AI
   */
  static async getHSCode(productData: AIProductRequest): Promise<AIProductData> {
    try {
      const response = await fetch(`${API_BASE_URL}/ai/product/hs-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(productData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      
      // Debug logging
      console.log('AI HS Code Response:', responseData);
      
      // Extract data from wrapped response
      const result: AIProductData = responseData.data || responseData;
      
      // Validate response structure
      if (typeof result.value !== 'string' || typeof result.confidence !== 'number') {
        console.error('Invalid HS code response structure:', {
          value: result.value,
          valueType: typeof result.value,
          confidence: result.confidence,
          confidenceType: typeof result.confidence,
          fullResponse: responseData
        });
        throw new Error('Invalid AI response format');
      }

      return result;
    } catch (error) {
      console.error('Error getting HS code from AI:', error);
      throw {
        message: error instanceof Error ? error.message : 'Failed to get HS code information',
        field: 'hsCode',
        type: error instanceof Error && error.message.includes('fetch') ? 'network' : 'ai'
      } as AIServiceError;
    }
  }

  /**
   * Get all product data fields at once for efficiency
   */
  static async getAllProductData(productData: AIProductRequest): Promise<AIBatchResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/ai/product/all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(productData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      
      // Debug logging
      console.log('AI Batch Response:', responseData);
      
      // Extract data from wrapped response
      const result: AIBatchResponse = responseData.data || responseData;
      
      // Validate response structure
      if (
        typeof result.manufacturer?.value !== 'string' || 
        typeof result.manufacturer?.confidence !== 'number' ||
        typeof result.originCountry?.value !== 'string' || 
        typeof result.originCountry?.confidence !== 'number' ||
        typeof result.hsCode?.value !== 'string' || 
        typeof result.hsCode?.confidence !== 'number'
      ) {
        console.error('Invalid batch response structure:', {
          manufacturer: result.manufacturer,
          originCountry: result.originCountry,
          hsCode: result.hsCode,
          fullResponse: responseData
        });
        throw new Error('Invalid AI batch response format');
      }

      return result;
    } catch (error) {
      console.error('Error getting batch product data from AI:', error);
      throw {
        message: error instanceof Error ? error.message : 'Failed to get complete product data',
        type: error instanceof Error && error.message.includes('fetch') ? 'network' : 'ai'
      } as AIServiceError;
    }
  }
}

/**
 * Utility functions for AI data processing
 */
export class AIDataProcessor {
  
  /**
   * Check if AI confidence is acceptable for auto-population
   */
  static isConfidenceAcceptable(confidence: number, threshold: number = 70): boolean {
    return confidence >= threshold;
  }

  /**
   * Format AI response for display in UI
   */
  static formatAIResponse(data: AIProductData): string {
    return `${data.value} (${data.confidence}% confidence)`;
  }

  /**
   * Get confidence level description
   */
  static getConfidenceDescription(confidence: number): string {
    if (confidence >= 90) return 'Very High';
    if (confidence >= 75) return 'High';
    if (confidence >= 60) return 'Medium';
    if (confidence >= 40) return 'Low';
    return 'Very Low';
  }

  /**
   * Get confidence color for UI display
   */
  static getConfidenceColor(confidence: number): string {
    if (confidence >= 90) return 'text-green-600';
    if (confidence >= 75) return 'text-blue-600';
    if (confidence >= 60) return 'text-yellow-600';
    if (confidence >= 40) return 'text-orange-600';
    return 'text-red-600';
  }

  /**
   * Validate product data before sending to AI
   */
  static validateProductData(productData: AIProductRequest): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!productData.name || productData.name.trim().length === 0) {
      errors.push('Product name is required');
    }

    if (productData.name && productData.name.trim().length < 3) {
      errors.push('Product name must be at least 3 characters long');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Sanitize AI response value for safe display
   */
  static sanitizeValue(value: string): string {
    return value.replace(/[<>\"'&]/g, '').trim();
  }

  /**
   * Extract country code from origin country response
   */
  static extractCountryCode(originCountry: string): string {
    // If it's already a 2-character code, return as is
    if (originCountry.length === 2) {
      return originCountry.toUpperCase();
    }
    
    // Common country mappings for Caribbean products
    const countryMappings: { [key: string]: string } = {
      'jamaica': 'JM',
      'barbados': 'BB',
      'trinidad': 'TT',
      'trinidad and tobago': 'TT',
      'guyana': 'GY',
      'united states': 'US',
      'usa': 'US',
      'canada': 'CA',
      'united kingdom': 'GB',
      'uk': 'GB'
    };
    
    const country = originCountry.toLowerCase();
    return countryMappings[country] || originCountry.toUpperCase().substring(0, 2);
  }
} 